// Default per-category configs. Mirrors the hardcoded field lists in
// functions/core/format_utils.py:update_body_html() — keep in sync.
// When no saved config exists in Firestore, these are used as fallback.

const makeColumn = (id, label, fieldPath, type = 'text', extra = {}) => ({
  id,
  label,
  fieldPath,
  type,
  visible: true,
  width: null,
  readOnly: false,
  ...extra,
});

const SHARED_COLUMNS = [
  makeColumn('img', 'Image', 'thumbnailUrls', 'image', { readOnly: true, width: 80 }),
  makeColumn('title', 'Title', 'consolidatedData.Title'),
  makeColumn('price', 'Price', 'consolidatedData.Price', 'currency'),
  makeColumn('condition', 'Condition', 'consolidatedData.Condition', 'select'),
  makeColumn('brand', 'Brand', 'consolidatedData.Brand'),
  makeColumn('allProductInfo', 'All Info', null, 'computed', { readOnly: true, visible: false }),
  makeColumn('shopifyType', 'Type', 'consolidatedData.Type', 'text', { shopifyOnly: true }),
  // Hidden by default; the Smartsheet-style layout turns it on.
  makeColumn('seoTitle', 'SEO Title', 'seoTitle', 'text', { visible: false }),
  // Editable, but locked per-row once the item is on eBay (eBay keys listings by SKU).
  makeColumn('sku', 'SKU', 'sku', 'text'),
  makeColumn('upc', 'UPC', 'upc', 'text', { visible: false }),
  makeColumn('location', 'Location', 'location', 'text', { visible: false }),
  makeColumn('otherPrices', 'Other Prices', 'priceOptions', 'computed', { readOnly: true }),
  makeColumn('shopifyAdminLink', 'Shopify Admin', 'shopifyData', 'computed', { readOnly: true, shopifyOnly: true }),
  makeColumn('shopifyLink', 'Shopify Link', 'shopifyData', 'computed', { readOnly: true, shopifyOnly: true, visible: false }),
  makeColumn('conditionDescription', 'Condition Desc.', 'conditionDescription', 'text', { readOnly: true }),
  makeColumn('shopifyCategory', 'Shopify Category', 'shopifyCategory.fullName', 'shopify-category'),
  // Editing disabled for now — see InlineEditCell's isCategoryReadOnly.
  makeColumn('ebayCategory', 'eBay Category', 'gptCategories.primaryCategory', 'ebay-category', { readOnly: true }),
  makeColumn('quantity', 'Qty', 'quantity', 'text', { width: 60 }),
  makeColumn('created', 'Created', 'created', 'date', { readOnly: true }),
  makeColumn('overallConfidence', 'Confidence', 'overallConfidence', 'percent', { readOnly: true, width: 90 }),
  makeColumn('shopify', 'Shopify', 'shopifyData', 'computed', { readOnly: true }),
  makeColumn('ebay', 'eBay', 'ebayData', 'computed', { readOnly: true }),
  makeColumn('square', 'Square', 'squareData', 'computed', { readOnly: true }),
  makeColumn('etsy', 'Etsy', 'etsyData', 'computed', { readOnly: true }),
  makeColumn('bodyHtml', 'Listing Preview', 'shopifyBodyHtml', 'computed', { readOnly: true }),
];

const makeBullet = (id, fieldPath, extra = {}) => ({
  id,
  fieldPath,
  format: null,
  specialType: null,
  candidates: [],
  enabled: true,
  ...extra,
});

// Columns added after customers may already have saved a config — merged into
// saved configs (hidden) by useProductConfig so they show up in the column menu.
export const LATER_ADDED_COLUMNS = SHARED_COLUMNS.filter((c) => ['upc', 'location', 'etsy', 'shopifyLink'].includes(c.id));

const BASE_BODY_HTML_TEMPLATE = {
  descriptionField: 'consolidatedData.Description',
  conditionField: 'consolidatedData.Condition',
  categoryField: 'consolidatedData.Category',
  appendJewelryCertBlurb: false,
  msrpField: 'consolidatedData.MSRP',
  bulletFields: [],
};

// ─── Handbags ────────────────────────────────────────────────────────────────
const handbagColumns = [
  ...SHARED_COLUMNS,
  makeColumn('exteriorMaterial', 'Exterior Material', 'consolidatedData.Exterior Material'),
  makeColumn('liningMaterial', 'Lining Material', 'consolidatedData.Lining Material'),
  makeColumn('features', 'Features', 'consolidatedData.Features'),
  makeColumn('hardwareMaterial', 'Hardware Material', 'consolidatedData.Hardware Material'),
  makeColumn('included', 'Included', 'consolidatedData.Included'),
  makeColumn('country', 'Country of Manufacture', 'consolidatedData.Country/Region of Manufacture'),
  makeColumn('dimensions', 'Dimensions', 'gptDimensionsData', 'dimensions'),
];

const handbagBodyHtml = {
  ...BASE_BODY_HTML_TEMPLATE,
  bulletFields: [
    makeBullet('ext_material', 'Exterior Material', { format: '<value> Exterior' }),
    makeBullet('lining_material', 'Lining Material', { format: '<value> Interior' }),
    makeBullet('features', 'Features'),
    makeBullet('hardware_material', 'Hardware Material'),
    makeBullet('included', 'Included'),
    makeBullet('dimensions', 'Dimensions', { specialType: 'dimensions' }),
    makeBullet('country', 'Country/Region of Manufacture', { specialType: 'country' }),
  ],
};

// ─── Watches ─────────────────────────────────────────────────────────────────
const watchColumns = [
  ...SHARED_COLUMNS,
  makeColumn('caseMaterial', 'Case Material', 'consolidatedData.Case Material'),
  makeColumn('bandMaterial', 'Band Material', 'consolidatedData.Band Material'),
  makeColumn('closure', 'Closure', 'consolidatedData.Closure'),
  makeColumn('display', 'Display', 'consolidatedData.Display'),
  makeColumn('bezelType', 'Bezel Type', 'consolidatedData.Bezel Type'),
  makeColumn('crystal', 'Crystal', 'consolidatedData.Crystal'),
  makeColumn('movement', 'Movement', 'consolidatedData.Movement'),
  makeColumn('waterResistance', 'Water Resistance', 'consolidatedData.Water Resistance'),
  makeColumn('referenceNumber', 'Reference Number', 'consolidatedData.Reference Number'),
  makeColumn('included', 'Included', 'consolidatedData.Included'),
  makeColumn('country', 'Country of Manufacture', 'consolidatedData.Country/Region of Manufacture'),
  makeColumn('yearManufactured', 'Year Manufactured', 'consolidatedData.Year Manufactured'),
];

const watchBodyHtml = {
  ...BASE_BODY_HTML_TEMPLATE,
  bulletFields: [
    makeBullet('case_material', 'Case Material'),
    makeBullet('band_closure', 'Band Material', { specialType: 'combo', candidates: ['Band Material', 'Closure'] }),
    makeBullet('display', 'Display'),
    makeBullet('bezel_type', 'Bezel Type'),
    makeBullet('crystal', 'Crystal'),
    makeBullet('movement', 'Movement'),
    makeBullet('water_resistance', 'Water Resistance'),
    makeBullet('reference_number', 'Reference Number'),
    makeBullet('included', 'Included'),
    makeBullet('country', 'Country/Region of Manufacture', { specialType: 'country' }),
    makeBullet('year_manufactured', 'Year Manufactured'),
  ],
};

// ─── Jewelry ─────────────────────────────────────────────────────────────────
const jewelryColumns = [
  ...SHARED_COLUMNS,
  makeColumn('metalPurity', 'Metal Purity', 'consolidatedData.Metal Purity'),
  makeColumn('itemWeight', 'Item Weight', 'consolidatedData.Item Weight'),
  makeColumn('coloredStones', 'Colored Stones', 'consolidatedData.Colored Stones'),
  makeColumn('totalCaratWeight', 'Total Carat Weight', 'consolidatedData.Total Carat Weight'),
  makeColumn('diamondCut', 'Diamond Cut', 'consolidatedData.Diamond Cut'),
  makeColumn('diamondColor', 'Diamond Color Grade', 'consolidatedData.Diamond Color Grade'),
  makeColumn('diamondClarity', 'Diamond Clarity Grade', 'consolidatedData.Diamond Clarity Grade'),
  makeColumn('ringSize', 'Ring Size', 'consolidatedData.Ring Size'),
  makeColumn('chainLength', 'Chain Length', 'consolidatedData.Chain Length'),
  makeColumn('pendantHeight', 'Pendant Height', 'consolidatedData.Pendant Height'),
  makeColumn('pendantWidth', 'Pendant Width', 'consolidatedData.Pendant Width'),
  makeColumn('certStatus', 'Certification Status', 'consolidatedData.Certification Status'),
  makeColumn('certification', 'Certification', 'consolidatedData.Certification'),
];

const jewelryBodyHtml = {
  ...BASE_BODY_HTML_TEMPLATE,
  appendJewelryCertBlurb: true,
  bulletFields: [
    makeBullet('metal_purity', 'Metal Purity'),
    makeBullet('item_weight', 'Item Weight', { specialType: 'item_weight' }),
    makeBullet('colored_stones', 'Colored Stones'),
    makeBullet('diamond_carat', 'Diamond Carat', { specialType: 'diamond_carat' }),
    makeBullet('diamond_cut', 'Diamond Cut'),
    makeBullet('diamond_color', 'Diamond Color Grade', { format: 'Diamond Color (<value>)' }),
    makeBullet('diamond_clarity', 'Diamond Clarity Grade', { format: 'Diamond Clarity (<value>)' }),
    makeBullet('ring_size', 'Ring Size', { format: 'US Size <value>' }),
    makeBullet('chain_length', 'Chain Length', { format: 'Chain Length: <value>' }),
    makeBullet('pendant_height', 'Pendant Height', { format: 'Height: <value>' }),
    makeBullet('pendant_width', 'Pendant Width', { format: 'Width: <value>' }),
    makeBullet('cert_status', 'Certification Status', { specialType: 'certification_status' }),
  ],
};

// ─── Shoes ───────────────────────────────────────────────────────────────────
const shoeColumns = [
  ...SHARED_COLUMNS,
  makeColumn('exteriorMaterial', 'Exterior Material', 'consolidatedData.Exterior Material'),
  makeColumn('shoeSize', 'Shoe Size', 'consolidatedData.Shoe Size'),
  makeColumn('heelHeight', 'Heel Height', 'consolidatedData.Height'),
  makeColumn('shaftHeight', 'Shaft Height', 'consolidatedData.Shaft Height'),
];

const shoeBodyHtml = {
  ...BASE_BODY_HTML_TEMPLATE,
  bulletFields: [
    makeBullet('ext_material', 'Exterior Material', { format: '<value> Exterior' }),
    makeBullet('shoe_size', 'Shoe Size', { format: 'Size <value>' }),
    makeBullet('heel_height', 'Height', { format: 'Heel Height: <value>' }),
    makeBullet('shaft_height', 'Shaft Height', { format: 'Shaft Height: <value>' }),
  ],
};

// ─── Default (everything else) ───────────────────────────────────────────────
const defaultColumns = [
  ...SHARED_COLUMNS,
  makeColumn('material', 'Material', 'consolidatedData.Exterior Material'),
  makeColumn('included', 'Included', 'consolidatedData.Included'),
  makeColumn('country', 'Country of Manufacture', 'consolidatedData.Country/Region of Manufacture'),
  makeColumn('dimensions', 'Dimensions', 'gptDimensionsData', 'dimensions'),
];

const defaultBodyHtml = {
  ...BASE_BODY_HTML_TEMPLATE,
  bulletFields: [
    makeBullet('material', 'Material', {
      specialType: 'first_of',
      candidates: ['Exterior Material', 'Hardware Material', 'Metal Type'],
    }),
    makeBullet('dimensions', 'Dimensions', { specialType: 'dimensions' }),
    makeBullet('included', 'Included'),
    makeBullet('country', 'Country/Region of Manufacture', { specialType: 'country' }),
  ],
};

// ─── Exported map ─────────────────────────────────────────────────────────────
export const DEFAULT_CATEGORY_CONFIGS = {
  handbags: {
    categoryKey: 'handbags',
    columns: handbagColumns,
    bodyHtmlTemplate: handbagBodyHtml,
  },
  watches: {
    categoryKey: 'watches',
    columns: watchColumns,
    bodyHtmlTemplate: watchBodyHtml,
  },
  jewelry: {
    categoryKey: 'jewelry',
    columns: jewelryColumns,
    bodyHtmlTemplate: jewelryBodyHtml,
  },
  shoes: {
    categoryKey: 'shoes',
    columns: shoeColumns,
    bodyHtmlTemplate: shoeBodyHtml,
  },
  default: {
    categoryKey: 'default',
    columns: defaultColumns,
    bodyHtmlTemplate: defaultBodyHtml,
  },
};
